import { useQuery } from "@tanstack/react-query";
import { from, rpc, unwrap, useOrg, type FileLink } from "@vixio/data";
import { registerAuditLabels, type StatusMap } from "@vixio/ui";
import type { InputKind, ToolKey } from "@vixio/pdf_tools/tools";

/** Tipos das linhas e RPCs do módulo pdf_tools (ver modules/pdf_tools/README.md). */

export type JobStatus = "aguardando_envio" | "na_fila" | "processando" | "concluido" | "falhou" | "cancelado" | "expirado";
export type Category = "organizar" | "converter" | "otimizar" | "seguranca" | "editar" | "integrar";

export const JOB_STATUS: StatusMap = {
  aguardando_envio: { label: "enviando arquivos", tone: "info" },
  na_fila: { label: "na fila", tone: "info" },
  processando: { label: "processando", tone: "info" },
  concluido: { label: "pronto", tone: "ok" },
  falhou: { label: "falhou", tone: "crit" },
  cancelado: { label: "cancelado", tone: "neutral" },
  expirado: { label: "expirado", tone: "neutral" },
};

export const CATEGORIES: Array<{ key: Category; label: string }> = [
  { key: "organizar", label: "Organizar páginas" },
  { key: "converter", label: "Converter" },
  { key: "otimizar", label: "Otimizar e reconhecer texto" },
  { key: "editar", label: "Editar" },
  { key: "seguranca", label: "Segurança" },
  { key: "integrar", label: "Validação e assinatura" },
];

export interface ToolInfo {
  key: ToolKey;
  label: string;
  description: string;
  category: Category;
  engine: "gotenberg" | "ocr" | "pdf_lib" | "vixio";
  input_kinds: InputKind[];
  min_files: number;
  max_files: number;
  team_only: boolean;
  secret_params: string[];
}

export interface PdfConfig {
  enabled: boolean;
  audience: "equipe" | "portal";
  tools: ToolInfo[];
  limits: { max_file_mb: number; max_job_mb: number; max_files: number; max_pages: number; retention_hours: number };
  usage: { used: number; quota: number | null } | null;
  accounts: Array<{ id: string; used: number; quota: number | null }>;
}

export interface JobFile {
  position: number;
  file_name: string;
  kind: string;
  content_type?: string;
  size_bytes: number | null;
  sha256: string | null;
  pages: number | null;
  bucket?: string;
}

export interface JobRow {
  id: string;
  org_id: string;
  tool: ToolKey;
  tool_label: string;
  engine: string;
  category: Category;
  audience: "equipe" | "portal";
  portal_account_id: string | null;
  requested_by: string;
  requested_by_email: string | null;
  status: JobStatus;
  params: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error: string | null;
  attempts: number;
  max_attempts: number;
  source_job_id: string | null;
  created_at: string;
  submitted_at: string | null;
  started_at: string | null;
  done_at: string | null;
  expires_at: string | null;
  purged_at: string | null;
  files_available: boolean;
  inputs: JobFile[];
  outputs: JobFile[];
}

export interface PdfSettings {
  org_id: string;
  disabled_tools: string[];
  portal_enabled: boolean;
  portal_disabled_tools: string[];
  max_file_mb: number;
  max_job_mb: number;
  max_files: number;
  max_pages: number;
  monthly_quota_org: number | null;
  monthly_quota_portal_account: number | null;
  retention_hours: number;
  timeout_seconds: number;
  notify_on_done: boolean;
  notify_after_seconds: number;
  gotenberg_url: string;
  gotenberg_username: string;
  ocr_url: string;
  configured: boolean;
  usage_month: number;
  updated_at: string;
}

export interface UsageRow {
  org_id: string;
  month: string;
  tool: string;
  tool_label: string;
  audience: string;
  jobs: number;
  done: number;
  failed: number;
  input_bytes: number;
}

export interface RequestResult {
  job_id: string;
  status: JobStatus;
  bucket: string;
  uploads: Array<{ position: number; name: string; path: string; content_type: string }>;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "pdf_tools.config": { args: { p_org: U }; returns: PdfConfig };
    "pdf_tools.get_settings": { args: { p_org: U }; returns: PdfSettings };
    "pdf_tools.set_settings": { args: { p_org: U; p_settings: Partial<Omit<PdfSettings, "org_id" | "configured" | "usage_month" | "updated_at">> }; returns: PdfSettings };
    "pdf_tools.request_job": {
      args: {
        p_org: U; p_tool: ToolKey; p_params?: Record<string, unknown>; p_files?: Array<{ name: string; size: number }>;
        p_account?: U | null; p_source_job?: U | null; p_source_positions?: number[] | null;
      };
      returns: RequestResult;
    };
    "pdf_tools.confirm_upload": { args: { p_job: U }; returns: { job_id: U; status: JobStatus } };
    "pdf_tools.cancel_job": { args: { p_job: U; p_reason?: string | null }; returns: null };
    "pdf_tools.retry_job": { args: { p_job: U }; returns: null };
    "pdf_tools.download_link": { args: { p_job: U; p_position?: number }; returns: FileLink & { size_bytes: number; content_type: string } };
  }
}

registerAuditLabels({
  "pdf_tools.job_requested": "Ferramenta PDF pedida",
  "pdf_tools.job_canceled": "Ferramenta PDF cancelada",
  "pdf_tools.job_retried": "Ferramenta PDF reprocessada",
  "pdf_tools.job_failed": "Ferramenta PDF falhou",
  "pdf_tools.file_downloaded": "Resultado de ferramenta PDF baixado",
  "pdf_tools.settings_updated": "Configurações das Ferramentas PDF alteradas",
});

export function usePdfConfig() {
  const org = useOrg();
  return useQuery({ queryKey: ["pdf_tools", "config", org.id], queryFn: () => rpc("pdf_tools", "config", { p_org: org.id }) });
}

const RUNNING: JobStatus[] = ["aguardando_envio", "na_fila", "processando"];
export const isRunning = (s: JobStatus) => RUNNING.includes(s);

/** Trabalhos recentes (a RLS decide: equipe vê a organização; cliente do portal, só os próprios). */
export function useJobs(limit = 50) {
  const org = useOrg();
  return useQuery({
    queryKey: ["pdf_tools", "jobs", org.id, limit],
    queryFn: () => unwrap<JobRow[]>(from("pdf_tools", "v_jobs").select("*").eq("org_id", org.id).order("created_at", { ascending: false }).limit(limit)),
    refetchInterval: (q) => ((q.state.data ?? []).some((j) => isRunning(j.status)) ? 2000 : false),
  });
}

export function useJob(id: string | null) {
  return useQuery({
    queryKey: ["pdf_tools", "job", id],
    enabled: Boolean(id),
    queryFn: async () => (await unwrap<JobRow[]>(from("pdf_tools", "v_jobs").select("*").eq("id", id!).limit(1)))[0] ?? null,
    refetchInterval: (q) => (q.state.data && isRunning(q.state.data.status) ? 1500 : false),
  });
}

/** Caminho base das telas: painel (/documentos/ferramentas-pdf) ou portal (/portal/ferramentas-pdf). */
export function basePath(portal: boolean): string {
  return portal ? "/portal/ferramentas-pdf" : "/documentos/ferramentas-pdf";
}
